import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import Spinner from '../common/Spinner';

interface AlterarSenhaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AlterarSenhaModal: React.FC<AlterarSenhaModalProps> = ({ isOpen, onClose }) => {
  const { usuario } = useAuth();
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Resetar o formulário quando o modal é aberto ou fechado
  useEffect(() => {
    if (isOpen) {
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
      setErro(null);
    }
  }, [isOpen]);
  
  const resetForm = () => {
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    setErro(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);

    // Validações
    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      setErro('Todos os campos são obrigatórios');
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setErro('A nova senha e a confirmação não coincidem');
      return;
    }

    if (novaSenha.length < 6) {
      setErro('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      setLoading(true);
      setErro(null);
      
      // Obter o token do localStorage
      const token = localStorage.getItem('bravobet_token');
      
      if (!token) {
        setErro('Sessão expirada. Faça login novamente.');
        setLoading(false);
        return;
      }
      
      // Chamar a API para alterar a senha
      const response = await api.post('/auth/alterar-senha', {
        senhaAtual,
        novaSenha
      });
      
      // O token é adicionado automaticamente pelo interceptor do axios

      if (response.data.success) {
        toast.success('Senha alterada com sucesso!');
        handleClose();
      } else {
        setErro(response.data.message || 'Erro ao alterar senha');
      }
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      setErro(error.response?.data?.message || 'Erro ao alterar senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const backdropClose = useBackdropClose(handleClose);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      {...backdropClose}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Alterar Senha</h2>
          <button 
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {erro && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {erro}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="senha-atual" className="block text-sm font-medium text-gray-700 mb-1">
              Senha Atual
            </label>
            <input
              id="senha-atual"
              type="password"
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
              placeholder="Digite sua senha atual"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="nova-senha" className="block text-sm font-medium text-gray-700 mb-1">
              Nova Senha
            </label>
            <input
              id="nova-senha"
              type="password"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
              placeholder="Digite sua nova senha"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="confirmar-senha" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar Nova Senha
            </label>
            <input
              id="confirmar-senha"
              type="password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
              placeholder="Confirme sua nova senha"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="mr-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-bravo-primary text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bravo-primary transition-colors flex items-center justify-center min-w-[120px]"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size={16} className="!text-white mr-2" />
                  Alterando...
                </>
              ) : 'Alterar Senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AlterarSenhaModal;
